/**
 * particles.js backgrounds for any element with class .particles-bg.
 *
 * Static import ensures window.particlesJS exists before this module runs its bootstrap.
 * CDN fallback only if the bundle path did not attach globals (rare).
 *
 * Inner wrapper matches reference sites:
 *   position:absolute; width:100%; height:100%; top:0; left:0;
 *
 * Override colours on the host:
 *   --particles-color, --particles-line-color (solid hex or rgb only — rgba alpha is ignored)
 *   --particles-opacity (0.05–1, default 0.65), --particles-line-opacity (0.05–1, default 0.75)
 * Density / spacing (optional — base values for a wide desktop banner; actual count/links use
 * min(area, width) vs reference so tall mobile heroes do not keep “desktop” particle counts):
 *   --particles-count (30–220, default 130)
 *   --particles-link-distance (80–320px, default 200) — higher = lines span farther, less “gaps” in the mesh
 *   --particles-size (2–12px dot radius-ish, default 6)
 */
import 'particles.js';

/**
 * particles.js assigns Object.deepExtend using arguments.callee for recursion.
 * ES modules (Vite `type="module"`) run in strict mode, where callee access throws,
 * so deep-merge of nested config never completes and the animation never starts.
 */
function deepExtendStrictSafe(destination, source) {
  if (!source || typeof source !== 'object') {
    return destination;
  }
  for (const property in source) {
    if (
      Object.prototype.hasOwnProperty.call(source, property) &&
      source[property] &&
      typeof source[property] === 'object' &&
      source[property].constructor === Object
    ) {
      destination[property] = destination[property] || {};
      deepExtendStrictSafe(destination[property], source[property]);
    } else {
      destination[property] = source[property];
    }
  }
  return destination;
}

Object.deepExtend = deepExtendStrictSafe;

function patchParticlesDeepExtend() {
  Object.deepExtend = deepExtendStrictSafe;
}

const PARTICLES_CDN = 'https://cdn.jsdelivr.net/npm/particles.js@2.0.0/particles.min.js';

let cdnPromise = null;

function trimCssVal(v) {
  return (v || '').trim();
}

/**
 * particles.js only parses hex in hexToRgb(); rgb()/var()/oklch() breaks dot + line colors.
 */
function cssColorToHex(raw, fallbackHex) {
  const s = trimCssVal(raw);
  if (!s) {
    return fallbackHex;
  }
  if (/^#[0-9a-f]{6}$/i.test(s)) {
    return s;
  }
  if (/^#[0-9a-f]{3}$/i.test(s)) {
    const h = s.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  const m = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) {
    const to = (n) => Math.max(0, Math.min(255, +n)).toString(16).padStart(2, '0');
    return `#${to(m[1])}${to(m[2])}${to(m[3])}`;
  }
  return fallbackHex;
}

function looksLikeWordPressDarkPreset(host) {
  if (!(host instanceof Element) || !host.classList) {
    return false;
  }
  return [...host.classList].some((c) =>
    /^has-(?:primary|secondary|accent|neutral)(?:-[\w-]+)?-background-color$/.test(c),
  );
}

function luminanceFromCssColor(css) {
  if (!css || css === 'transparent') {
    return null;
  }
  const m = css.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/);
  if (!m) {
    return null;
  }
  const alpha = m[4] !== undefined ? parseFloat(m[4]) : 1;
  if (alpha < 0.08) {
    return null;
  }
  const r = +m[1];
  const g = +m[2];
  const b = +m[3];
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function intFromCssVar(host, name, fallback, min, max) {
  if (!(host instanceof Element)) {
    return fallback;
  }
  const raw = trimCssVal(getComputedStyle(host).getPropertyValue(name));
  if (!raw) {
    return fallback;
  }
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) {
    return fallback;
  }
  return Math.max(min, Math.min(max, n));
}

function floatFromCssVar(host, name, fallback, min, max) {
  if (!(host instanceof Element)) {
    return fallback;
  }
  const raw = trimCssVal(getComputedStyle(host).getPropertyValue(name));
  if (!raw) {
    return fallback;
  }
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) {
    return fallback;
  }
  return Math.max(min, Math.min(max, n));
}

function clampNum(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

/** Reference surface (px²) for “desktop-sized” banner; used together with width (below). */
const LAYOUT_REF_AREA_PX = 500 * 560;
/** ~ comfortable desktop content width; narrow viewports cap particle budget so tall mobile heroes are not packed. */
const LAYOUT_REF_WIDTH_PX = 760;

/**
 * Crowding follows horizontal space: a 390×900px hero has almost the same area as a desktop strip but
 * much less width, so we take min(area ratio, width ratio) instead of area alone.
 */
function layoutDensityFactor(host) {
  if (!(host instanceof Element)) {
    return 1;
  }
  const r = host.getBoundingClientRect();
  const w = Math.max(1, Math.round(r.width));
  const h = Math.max(1, Math.round(r.height));
  const area = w * h;
  const byArea = area / LAYOUT_REF_AREA_PX;
  const byWidth = w / LAYOUT_REF_WIDTH_PX;
  const raw = Math.min(byArea, byWidth);
  return clampNum(raw, 0.2, 1.2);
}

/** Coarse bucket so ResizeObserver does not remount on sub-pixel jitter. */
function layoutBucketKey(host) {
  if (!(host instanceof Element)) {
    return '';
  }
  const r = host.getBoundingClientRect();
  const w = Math.max(1, Math.round(r.width));
  const h = Math.max(1, Math.round(r.height));
  const bw = Math.round(w / 32) * 32;
  const bh = Math.round(h / 32) * 32;
  return `${bw}x${bh}`;
}

function layoutFromHost(host) {
  const baseCount = intFromCssVar(host, '--particles-count', 130, 30, 220);
  const baseLink = intFromCssVar(host, '--particles-link-distance', 200, 80, 320);
  const baseSize = intFromCssVar(host, '--particles-size', 6, 2, 12);
  const f = layoutDensityFactor(host);

  const count = Math.round(baseCount * f);
  const linkDistance = Math.round(baseLink * Math.pow(f, 0.42));
  const dotSize = Math.round(baseSize * (0.85 + 0.15 * clampNum(f, 0.35, 1)));

  return {
    count: clampNum(count, 22, 220),
    linkDistance: clampNum(linkDistance, 68, 320),
    dotSize: clampNum(dotSize, 2, 12),
  };
}

function colorsFromHost(host) {
  const hostCs = getComputedStyle(host);
  let dot = trimCssVal(hostCs.getPropertyValue('--particles-color'));
  let lineBase = trimCssVal(hostCs.getPropertyValue('--particles-line-color'));
  const rootCs = getComputedStyle(document.documentElement);
  const lightFallback = '#ffffff';
  const darkFallback = '#4f46e5';

  if (!dot) {
    const bg = hostCs.backgroundColor;
    const lum = luminanceFromCssColor(bg);
    const useLight =
      looksLikeWordPressDarkPreset(host) || (lum !== null && lum < 0.55);

    if (useLight) {
      dot = lightFallback;
      lineBase = lightFallback;
    } else {
      dot = cssColorToHex(rootCs.getPropertyValue('--color-primary'), darkFallback);
      if (!lineBase) {
        lineBase = dot;
      }
    }
  }

  dot = cssColorToHex(dot, lightFallback);
  lineBase = cssColorToHex(lineBase || dot, dot);

  return { dot, lineBase };
}

function buildConfig(host) {
  const { dot, lineBase } = colorsFromHost(host);
  const { count, linkDistance, dotSize } = layoutFromHost(host);
  const dotOpacity = floatFromCssVar(host, '--particles-opacity', 0.65, 0.05, 1);
  const lineOpacity = floatFromCssVar(host, '--particles-line-opacity', 0.75, 0.05, 1);

  return {
    particles: {
      /*
       * densityAutoParticles() uses canvas width×height; at first paint that is often 0×0 when the
       * canvas only has CSS % sizing, so the library removes every particle. Use a computed count
       * (from CSS base × layout: min(area vs ref, width vs ref) so tall narrow mobile heroes thin out).
       */
      number: { value: count, density: { enable: false, value_area: 800 } },
      color: { value: dot },
      shape: {
        type: 'circle',
        stroke: { width: 0, color: dot },
        polygon: { nb_sides: 5 },
        image: { src: '', width: 100, height: 100 },
      },
      opacity: {
        value: dotOpacity,
        random: false,
        anim: { enable: false, speed: 1, opacity_min: 0.9, sync: false },
      },
      size: {
        value: dotSize,
        random: true,
        anim: { enable: false, speed: 40, size_min: 0.1, sync: false },
      },
      line_linked: {
        enable: true,
        distance: linkDistance,
        color: lineBase,
        opacity: lineOpacity,
        width: 2,
      },
      move: {
        enable: true,
        speed: 0.5,
        direction: 'none',
        random: false,
        straight: false,
        out_mode: 'out',
        bounce: false,
        attract: { enable: false, rotateX: 600, rotateY: 1200 },
      },
    },
    interactivity: {
      detect_on: 'canvas',
      events: {
        onhover: { enable: true, mode: 'repulse', speed: 0.35, delay: 0.2 },
        onclick: { enable: true, mode: 'push', speed: 0.35, delay: 0.2 },
        resize: false,
      },
      modes: {
        grab: { distance: 400, line_linked: { opacity: 0.4 } },
        bubble: { distance: 400, size: 40, duration: 2, opacity: 8, speed: 3 },
        repulse: { distance: 120, duration: 0.4 },
        push: { particles_nb: 4 },
        remove: { particles_nb: 2 },
      },
    },
    retina_detect: true,
  };
}

function cdnScriptPromise() {
  return new Promise((resolve, reject) => {
    if (typeof window.particlesJS === 'function') {
      patchParticlesDeepExtend();
      resolve();
      return;
    }
    const prev = document.querySelector('script[data-pvtl-particles-lib]');
    if (prev) {
      prev.addEventListener('load', () => {
        patchParticlesDeepExtend();
        resolve();
      }, { once: true });
      prev.addEventListener(
        'error',
        () => reject(new Error('particles CDN load error')),
        { once: true },
      );
      return;
    }
    const s = document.createElement('script');
    s.src = PARTICLES_CDN;
    s.async = true;
    s.dataset.pvtlParticlesLib = '1';
    s.onload = () => {
      patchParticlesDeepExtend();
      resolve();
    };
    s.onerror = () => reject(new Error('particles CDN load error'));
    document.head.appendChild(s);
  });
}

function ensureParticlesLib() {
  patchParticlesDeepExtend();
  if (typeof window.particlesJS === 'function') {
    return Promise.resolve();
  }
  if (cdnPromise) {
    return cdnPromise;
  }
  cdnPromise = cdnScriptPromise()
    .then(() => {
      if (typeof window.particlesJS !== 'function') {
        cdnPromise = null;
        throw new Error('particlesJS missing after CDN load');
      }
    })
    .catch((e) => {
      cdnPromise = null;
      throw e;
    });
  return cdnPromise;
}

function uniqueId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `pjs-${crypto.randomUUID().replace(/-/g, '')}`;
  }
  return `pjs-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * One id per .particles-bg host (stored on the host). Holder id becomes `#id` in particles.js
 * `querySelector('#'+tag_id+' > …')`; dots/colons break that selector, so we only reuse
 * `host.id` when it is a safe fragment.
 */
function isSafeDerivedHolderIdFragment(s) {
  return typeof s === 'string' && /^[A-Za-z][A-Za-z0-9_-]*$/.test(s);
}

function stableHolderId(host) {
  const cached = host.getAttribute('data-particles-holder-id');
  if (cached) {
    return cached;
  }

  let id;
  const anchor = host.id && isSafeDerivedHolderIdFragment(host.id) ? host.id : '';
  if (anchor && !document.getElementById(`pjs-${anchor}`)) {
    id = `pjs-${anchor}`;
  } else {
    id = uniqueId();
  }

  host.setAttribute('data-particles-holder-id', id);
  return id;
}

/** particlesJS uses document.getElementById; duplicates append the canvas to the wrong node. */
function ensureHolderIdIsUnique(holder, host) {
  if (!holder.id) {
    holder.id = stableHolderId(host);
  }
  const el = document.getElementById(holder.id);
  if (el === holder) {
    return;
  }
  if (el !== holder && (el !== null || holder.isConnected)) {
    const next = uniqueId();
    holder.id = next;
    host.setAttribute('data-particles-holder-id', next);
  }
}

function kickResize() {
  window.dispatchEvent(new Event('resize'));
}

function findDirectHolder(host) {
  for (let i = 0; i < host.children.length; i += 1) {
    const c = host.children[i];
    if (c.classList.contains('particles-bg__holder')) {
      return c;
    }
  }
  return null;
}

/* inset:0 fills the positioned parent; width/height 100% can collapse to 0px before layout */
const HOLDER_STYLE =
  'position:absolute;inset:0;z-index:0;width:auto;height:auto;pointer-events:none;';

function refreshParticlesForTagId(tagId) {
  const tag = document.getElementById(tagId);
  const dom = window.pJSDom;
  if (!tag || !Array.isArray(dom)) {
    return;
  }
  for (let i = dom.length - 1; i >= 0; i -= 1) {
    const item = dom[i];
    const canvasEl = item?.pJS?.canvas?.el;
    if (canvasEl && tag.contains(canvasEl) && typeof item.pJS?.fn?.particlesRefresh === 'function') {
      try {
        item.pJS.fn.particlesRefresh();
      } catch (e) {
        console.warn('particles-bg: refresh failed', e);
      }
      return;
    }
  }
}

/** First child should be the particles layer so z-index stacking matches the design. */
function ensureHolderElement(host) {
  let holder = findDirectHolder(host);
  const id = stableHolderId(host);
  if (holder) {
    if (holder !== host.firstElementChild) {
      host.insertBefore(holder, host.firstChild);
    }
    if (holder.id !== id) {
      holder.id = id;
    }
    return holder;
  }
  holder = document.createElement('div');
  holder.className = 'particles-bg__holder';
  holder.id = id;
  holder.setAttribute('aria-hidden', 'true');
  holder.style.cssText = HOLDER_STYLE;
  host.insertBefore(holder, host.firstChild);
  return holder;
}

/** Absolute layer can still compute 0×0 before layout; nudge so canvas.offsetWidth/height are non-zero. */
function forceHolderLayout(host, holder) {
  const hr = host.getBoundingClientRect();
  const ch = Math.max(host.clientHeight, Math.round(hr.height), 1);
  const cw = Math.max(host.clientWidth, Math.round(hr.width), 1);
  if (holder.clientHeight < 6 || holder.clientWidth < 6) {
    holder.style.minHeight = `${Math.max(120, ch)}px`;
    holder.style.minWidth = `${Math.max(120, cw)}px`;
  }
}

const resizeObserveTimers = new WeakMap();

function scheduleParticlesResizeHandling(host) {
  if (!(host instanceof Element)) {
    return;
  }
  const prev = resizeObserveTimers.get(host);
  if (prev !== undefined) {
    window.clearTimeout(prev);
  }
  resizeObserveTimers.set(
    host,
    window.setTimeout(() => {
      resizeObserveTimers.delete(host);
      if (!host.isConnected) {
        return;
      }
      if (host.hasAttribute('data-particles-init')) {
        const next = layoutBucketKey(host);
        const prevKey = host.getAttribute('data-particles-layout-bucket');
        if (prevKey && prevKey !== next) {
          host.removeAttribute('data-particles-init');
          host.removeAttribute('data-particles-layout-bucket');
          const holder = findDirectHolder(host);
          holder?.querySelector(':scope > canvas.particles-js-canvas-el')?.remove();
          initParticlesBackgrounds();
        }
        return;
      }
      initParticlesBackgrounds();
    }, 140),
  );
}

function attachResizeObservers() {
  if (typeof ResizeObserver === 'undefined') {
    return;
  }
  document.querySelectorAll('.particles-bg:not([data-particles-ro])').forEach((host) => {
    host.setAttribute('data-particles-ro', '1');
    const ro = new ResizeObserver(() => {
      if (!host.isConnected) {
        return;
      }
      scheduleParticlesResizeHandling(host);
    });
    ro.observe(host);
  });
}

let domObserverAttached = false;

/**
 * Ignore mutations inside .particles-bg__holder (canvas append/remove, etc.) so we do not
 * spin a tight loop or thrash DevTools. Only care about new hosts or .particles-bg class.
 */
function mutationMightNeedNewMount(records) {
  for (const r of records) {
    const t = r.target;
    if (!(t instanceof Element)) {
      continue;
    }

    if (t.classList.contains('particles-bg__holder') || t.closest('.particles-bg__holder')) {
      continue;
    }

    if (r.type === 'attributes' && r.attributeName === 'class') {
      if (t.classList.contains('particles-bg') && !t.hasAttribute('data-particles-init')) {
        return true;
      }
      continue;
    }

    if (r.type === 'childList' && r.addedNodes.length > 0) {
      for (const n of r.addedNodes) {
        if (!(n instanceof Element)) {
          continue;
        }
        if (n.matches?.('.particles-bg') && !n.hasAttribute('data-particles-init')) {
          return true;
        }
        if (n.querySelector?.('.particles-bg:not([data-particles-init])')) {
          return true;
        }
      }
    }
  }
  return false;
}

/** Late-injected markup (plugins, hybrid themes) may add .particles-bg after first paint. */
function attachDomMutationObserver() {
  if (domObserverAttached || !document.body) {
    return;
  }
  domObserverAttached = true;
  let debounceT = 0;
  const mo = new MutationObserver((records) => {
    if (typeof window.particlesJS !== 'function') {
      return;
    }
    if (!mutationMightNeedNewMount(records)) {
      return;
    }
    window.clearTimeout(debounceT);
    debounceT = window.setTimeout(() => {
      attachResizeObservers();
      initParticlesBackgrounds();
    }, 32);
  });
  mo.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
}

function mountOne(host) {
  const run = window.particlesJS;
  if (typeof run !== 'function') {
    return;
  }

  const holder = ensureHolderElement(host);
  ensureHolderIdIsUnique(holder, host);
  forceHolderLayout(host, holder);

  const existingCanvas = holder.querySelector(':scope > canvas.particles-js-canvas-el');
  if (host.hasAttribute('data-particles-init') && existingCanvas) {
    return;
  }
  if (host.hasAttribute('data-particles-init')) {
    host.removeAttribute('data-particles-init');
  }

  const config = buildConfig(host);

  const fire = () => {
    if (!host.isConnected) {
      return;
    }
    forceHolderLayout(host, holder);
    ensureHolderIdIsUnique(holder, host);
    try {
      run(holder.id, config);
    } catch (e) {
      host.setAttribute('data-particles-error', String(e?.message || e).slice(0, 160));
      console.warn('particles-bg: init failed', e);
      return;
    }

    const canvas = holder.querySelector(':scope > canvas.particles-js-canvas-el');
    if (!canvas) {
      const msg =
        'particlesJS finished but canvas not in holder (check duplicate id #'
          + holder.id
          + ' or DOM mismatch)';
      host.setAttribute('data-particles-error', msg.slice(0, 160));
      console.warn('particles-bg:', msg, {
        holderId: holder.id,
        getElementById: document.getElementById(holder.id),
      });
      return;
    }

    host.removeAttribute('data-particles-error');
    host.setAttribute('data-particles-init', 'true');
    host.setAttribute('data-particles-layout-bucket', layoutBucketKey(host));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        kickResize();
        refreshParticlesForTagId(holder.id);
        window.setTimeout(() => {
          const c = holder.querySelector(':scope > canvas.particles-js-canvas-el');
          if (c && (c.width < 2 || c.height < 2)) {
            refreshParticlesForTagId(holder.id);
          }
        }, 200);
      });
    });
  };

  requestAnimationFrame(fire);
}

export function initParticlesBackgrounds() {
  if (typeof window.particlesJS !== 'function') {
    return;
  }

  try {
    document.querySelectorAll('.particles-bg').forEach((host) => {
      if (host instanceof Element) {
        mountOne(host);
      }
    });
  } catch (e) {
    console.warn('particles-bg: initParticlesBackgrounds', e);
  }
}

function scheduleInitRetries() {
  requestAnimationFrame(() => initParticlesBackgrounds());
  window.setTimeout(initParticlesBackgrounds, 50);
  window.setTimeout(initParticlesBackgrounds, 400);
  window.setTimeout(initParticlesBackgrounds, 2000);
}

function startParticlesBootstrap() {
  void ensureParticlesLib()
    .then(() => {
      try {
        attachResizeObservers();
        attachDomMutationObserver();
        initParticlesBackgrounds();
        scheduleInitRetries();
      } catch (e) {
        console.warn('particles-bg: bootstrap', e);
      }
    })
    .catch((e) => {
      console.warn('particles-bg:', e.message || e);
    });
}

function whenDocumentReady(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    queueMicrotask(fn);
  }
}

queueMicrotask(() => {
  try {
    if (typeof window.particlesJS === 'function') {
      initParticlesBackgrounds();
    }
  } catch (e) {
    console.warn('particles-bg: early', e);
  }
});

whenDocumentReady(startParticlesBootstrap);

function afterFullLoadKick() {
  void ensureParticlesLib()
    .then(() => {
      try {
        attachResizeObservers();
        document.querySelectorAll('.particles-bg').forEach((host) => {
          if (host instanceof Element) {
            ensureHolderElement(host);
          }
        });
        initParticlesBackgrounds();
        kickResize();
      } catch (e) {
        console.warn('particles-bg: after load', e);
      }
    })
    .catch(() => {});
}

function scheduleAfterLoad(fn) {
  if (document.readyState === 'complete') {
    queueMicrotask(fn);
  } else {
    window.addEventListener('load', fn, { once: true });
  }
}

scheduleAfterLoad(afterFullLoadKick);

/* Debug: pvtlInitParticles() re-runs mount (e.g. stale cache) */
if (typeof window !== 'undefined') {
  window.pvtlInitParticles = () => {
    startParticlesBootstrap();
    scheduleAfterLoad(afterFullLoadKick);
  };
}
